/**
 * Parser and shape detection for analyze_topic tool results. Kept separate
 * from AnalyzeTopicOutput.tsx so the parser is unit-testable without React
 * fast-refresh complaining about non-component exports living next to
 * components.
 */

/**
 * Parsed shape of an analyze_topic TOON body. Numbers stay as their original
 * textual representation so 64-bit nanosecond timestamps survive intact (JS
 * Number truncates above 2^53). The renderer prints values verbatim.
 */
export interface ToonTable {
  columns: string[];
  rowCount: number;
  rows: (string | boolean | null)[][];
}

/**
 * Detection + parsed payload for whatever the chat UI received as
 * analyze_topic output. The detection rules are deliberately conservative —
 * any text that doesn't begin with the `topic_ids:` preamble falls through to
 * the plaintext branch so error strings (e.g. "Topic not found.") render as
 * authored.
 */
export type AnalyzeTopicPayload =
  | { kind: "toon"; topicIds: string[]; table: ToonTable }
  | { kind: "legacy-json"; topicIds: string[]; table: ToonTable }
  | { kind: "text"; text: string }
  | { kind: "unknown"; raw: unknown };

const _TOON_PREAMBLE =
  /^topic_ids:\s*(\[[^\n]*\])\s*\n+(data\[\d+\]\{[^\n]*\}:[\s\S]*)$/;
const _TOON_HEADER = /^data\[(\d+)\]\{(.*)\}:\s*$/;

function _isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Tokenize one TOON row body (no leading indent). Quoted strings honor
 * ``\"``, ``\\``, ``\n``, ``\r``, ``\t`` escapes; bare ``null``/``true``/
 * ``false`` collapse to the corresponding JS scalars; everything else is
 * preserved as its original text so numeric precision is never lost in
 * display.
 */
export function parseToonRow(line: string): (string | boolean | null)[] {
  const values: (string | boolean | null)[] = [];
  let i = 0;
  while (i <= line.length) {
    while (i < line.length && (line[i] === " " || line[i] === "\t")) {
      i++;
    }
    if (i >= line.length) {
      break;
    }

    if (line[i] === '"') {
      i++;
      let s = "";
      while (i < line.length) {
        const ch = line[i];
        if (ch === "\\" && i + 1 < line.length) {
          const next = line[i + 1];
          if (next === "n") {
            s += "\n";
          } else if (next === "r") {
            s += "\r";
          } else if (next === "t") {
            s += "\t";
          } else {
            s += next;
          }
          i += 2;
        } else if (ch === '"') {
          i++;
          break;
        } else {
          s += ch;
          i++;
        }
      }
      values.push(s);
    } else {
      let s = "";
      while (i < line.length && line[i] !== ",") {
        s += line[i];
        i++;
      }
      const trimmed = s.trim();
      if (trimmed === "null") {
        values.push(null);
      } else if (trimmed === "true") {
        values.push(true);
      } else if (trimmed === "false") {
        values.push(false);
      } else {
        values.push(trimmed);
      }
    }

    if (i < line.length && line[i] === ",") {
      i++;
    } else {
      break;
    }
  }
  return values;
}

/**
 * Parse a full TOON ``data[N]{cols}:\n  row...`` block. Returns ``null`` if the
 * header line doesn't match — callers treat that as "not TOON, fall back".
 */
export function parseToon(toonText: string): ToonTable | null {
  const lines = toonText.split("\n");
  if (lines.length === 0) {
    return null;
  }
  const headerMatch = _TOON_HEADER.exec(lines[0]);
  if (!headerMatch) {
    return null;
  }
  const rowCount = Number.parseInt(headerMatch[1], 10);
  const columnRaw = headerMatch[2];
  const columnValues = parseToonRow(columnRaw);
  const columns = columnValues.map((v) => {
    if (v === null) {
      return "";
    }
    if (typeof v === "boolean") {
      return String(v);
    }
    return v;
  });

  const rows: (string | boolean | null)[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    // Body lines are indented; blank lines are skipped (defensive — the
    // encoder never emits them, but the LLM may forward a slightly mangled
    // payload).
    const body = raw.replace(/^\s+/, "");
    if (body.length === 0) {
      continue;
    }
    rows.push(parseToonRow(body));
  }
  return { columns, rowCount, rows };
}

/**
 * Coerce a legacy JSON ``data`` array (list of row objects) into the same
 * shape ``parseToon`` produces. Preserves existing-chat rendering while the
 * new TOON path is the default for fresh queries.
 */
function _legacyDataToTable(data: unknown[]): ToonTable {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of data) {
    if (_isPlainObject(row)) {
      for (const key of Object.keys(row)) {
        if (!seen.has(key)) {
          seen.add(key);
          columns.push(key);
        }
      }
    }
  }
  const rows: (string | boolean | null)[][] = data.map((row) => {
    if (!_isPlainObject(row)) {
      return columns.map(() => null);
    }
    return columns.map((col) => {
      const v = row[col];
      if (v === null || v === undefined) {
        return null;
      }
      if (typeof v === "boolean") {
        return v;
      }
      if (typeof v === "number" || typeof v === "bigint") {
        return String(v);
      }
      if (typeof v === "string") {
        return v;
      }
      return JSON.stringify(v);
    });
  });
  return { columns, rowCount: rows.length, rows };
}

/**
 * Decide what kind of analyze_topic payload we have so the renderer can pick
 * a layout. The string branch eats any leading whitespace before the
 * ``topic_ids:`` preamble so the LLM can't break detection by reflowing the
 * payload through an intermediate format.
 */
export function detectAnalyzeTopicPayload(
  output: unknown,
): AnalyzeTopicPayload {
  if (typeof output === "string") {
    const match = _TOON_PREAMBLE.exec(output.trim());
    if (match) {
      let topicIds: unknown;
      try {
        topicIds = JSON.parse(match[1]);
      } catch {
        return { kind: "text", text: output };
      }
      if (
        !Array.isArray(topicIds) ||
        !topicIds.every((id) => typeof id === "string")
      ) {
        return { kind: "text", text: output };
      }
      const table = parseToon(match[2]);
      if (!table) {
        return { kind: "text", text: output };
      }
      return { kind: "toon", topicIds, table };
    }
    return { kind: "text", text: output };
  }

  if (_isPlainObject(output)) {
    // ``extractOutput`` in ToolDetailPanel leaves ``{json: ...}`` content
    // blocks intact when the response isn't a text block; unwrap once so
    // legacy ``{topic_ids, data}`` payloads from pre-TOON chats are detected.
    const unwrapped = _isPlainObject(output.json) ? output.json : output;
    const topicIds = unwrapped.topic_ids;
    const data = unwrapped.data;
    if (
      Array.isArray(topicIds) &&
      topicIds.every((id) => typeof id === "string") &&
      Array.isArray(data)
    ) {
      return {
        kind: "legacy-json",
        topicIds,
        table: _legacyDataToTable(data),
      };
    }
  }

  return { kind: "unknown", raw: output };
}
